import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import { canWrite } from '../../rbac';
import { gramsabhaAPI, contentAPI } from '../../services/api';
import useBackButtonClose from '../../hooks/useBackButtonClose';
import { formatMarathiDate } from '../../utils/marathi';
import LoadingSpinner from '../../components/common/LoadingSpinner';
import './Gramsabha.css';

const STATUS_LABELS = {
  scheduled: 'नियोजित',
  in_progress: 'प्रगतीपथावर',
  completed: 'संपन्न',
  cancelled: 'रद्द',
};

const STATUS_COLORS = {
  scheduled: 'warning',
  in_progress: 'info',
  completed: 'success',
  cancelled: 'danger',
};

const EMPTY_FORM = {
  title: '',
  meeting_type: 'regular',
  status: 'scheduled',
  meeting_date: '',
  venue: '',
  agenda: '',
  description: '',
  attachment_url: '',
};

const EMPTY_RESOLUTION = {
  title: '',
  description: '',
  proposed_by: '',
  seconded_by: '',
  result: 'passed',
  votes_for: '',
  votes_against: '',
  votes_abstain: '',
};

export default function Gramsabha() {
  const { tenant, user } = useAuth();
  const slug = tenant?.slug;
  const canWriteThis = canWrite(user?.role, 'gramsabha', user);
  const [meetings, setMeetings] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [selected, setSelected] = useState(null);
  const [success, setSuccess] = useState('');
  const [error, setError] = useState('');
  const [formErrors, setFormErrors] = useState({});

  const [formData, setFormData] = useState(EMPTY_FORM);
  const [resolutionData, setResolutionData] = useState(EMPTY_RESOLUTION);
  const [uploadingNotice, setUploadingNotice] = useState(false);

  // MoM state for selected meeting
  const [momSummary, setMomSummary] = useState('');
  const [momAttachmentUrl, setMomAttachmentUrl] = useState('');
  const [uploadingMom, setUploadingMom] = useState(false);
  const [sendingMom, setSendingMom] = useState(false);

  const closeForm = useCallback(() => {
    setShowForm(false);
    setSelected(null);
    setFormData(EMPTY_FORM);
    setFormErrors({});
    setError('');
  }, []);

  useBackButtonClose(showForm, closeForm);

  const splitDateTime = (value) => {
    const [date = '', time = ''] = String(value || '').split('T');
    return { date, time: time ? `${time}:00`.slice(0, 8) : '' };
  };

  const extractError = (err) => {
    const details = err.response?.data?.details;
    if (Array.isArray(details) && details.length) {
      return details.map(item => item.message).join(', ');
    }
    return err.response?.data?.error || 'अयशस्वी';
  };

  const validateForm = () => {
    const errors = {};
    const { date, time } = splitDateTime(formData.meeting_date);
    if (!formData.title.trim()) errors.title = 'शीर्षक आवश्यक आहे';
    if (!date || !time) errors.meeting_date = 'तारीख आणि वेळ आवश्यक आहे';
    if (!formData.venue.trim()) errors.venue = 'स्थळ आवश्यक आहे';
    return errors;
  };

  const buildPayload = () => {
    const { date, time } = splitDateTime(formData.meeting_date);
    return {
      title: formData.title.trim(),
      meeting_type: formData.meeting_type,
      status: formData.status || 'scheduled',
      meeting_date: date,
      meeting_time: time,
      venue: formData.venue.trim(),
      agenda: formData.agenda.split(/\r?\n|,/).map(item => item.trim()).filter(Boolean),
      description: formData.description,
      attachment_url: formData.attachment_url || null,
    };
  };

  const setField = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
    if (formErrors[field]) {
      setFormErrors(prev => ({ ...prev, [field]: undefined }));
    }
  };

  const openCreate = () => {
    setShowForm(true);
    setSelected(null);
    setFormData(EMPTY_FORM);
    setFormErrors({});
    setError('');
    setSuccess('');
  };

  const loadData = useCallback(async () => {
    if (!slug) return;
    setLoading(true);
    try {
      const res = await gramsabhaAPI.getAll(slug);
      setMeetings(res.data.meetings || []);
    } catch (err) {
      setError('लोड करण्यात अयशस्वी');
    } finally {
      setLoading(false);
    }
  }, [slug]);

  useEffect(() => { loadData(); }, [loadData]);

  const handleNoticeUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploadingNotice(true);
    setError('');
    try {
      const data = new FormData();
      data.append('file', file);
      const res = await contentAPI.uploadFile(slug, data);
      const url = res.data?.url || res.data?.fileUrl;
      if (url) {
        setFormData(prev => ({ ...prev, attachment_url: url }));
      } else {
        setError('नोटीस फाईल अपलोड अयशस्वी');
      }
    } catch (err) {
      setError('नोटीस फाईल अपलोड अयशस्वी: ' + (err.response?.data?.error || err.message));
    } finally {
      setUploadingNotice(false);
      e.target.value = '';
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setSuccess('');
    const errors = validateForm();
    setFormErrors(errors);
    if (Object.keys(errors).length) return;

    try {
      await gramsabhaAPI.create(slug, buildPayload());
      setSuccess('सभा नियोजित केली, सार्वजनिक सूचना प्रकाशित केली आणि WhatsApp वर पाठवली जात आहे!');
      closeForm();
      loadData();
    } catch (err) {
      setError(extractError(err));
    }
  };

  const handleResolution = async (e) => {
    e.preventDefault();
    try {
      await gramsabhaAPI.addResolution(slug, selected.id, {
        ...resolutionData,
        votes_for: parseInt(resolutionData.votes_for, 10) || 0,
        votes_against: parseInt(resolutionData.votes_against, 10) || 0,
        votes_abstain: parseInt(resolutionData.votes_abstain, 10) || 0,
      });
      setSuccess('ठराव यशस्वीरित्या जोडला!');
      setResolutionData(EMPTY_RESOLUTION);
      const res = await gramsabhaAPI.getById(slug, selected.id);
      setSelected(res.data.meeting);
    } catch (err) {
      setError(extractError(err) || 'ठराव जोडण्यात अयशस्वी');
    }
  };

  const viewDetails = async (id) => {
    try {
      const res = await gramsabhaAPI.getById(slug, id);
      const m = res.data.meeting;
      setSelected(m);
      setMomSummary(m.minutes || '');
      setMomAttachmentUrl(m.mom_attachment_url || '');
      setError('');
      setSuccess('');
    } catch (err) {
      setError('तपशील लोड करण्यात अयशस्वी');
    }
  };

  const handleMomUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploadingMom(true);
    setError('');
    try {
      const data = new FormData();
      data.append('file', file);
      const res = await contentAPI.uploadFile(slug, data);
      const url = res.data?.url || res.data?.fileUrl;
      if (url) {
        setMomAttachmentUrl(url);
      } else {
        setError('इतिवृत्त फाईल अपलोड अयशस्वी');
      }
    } catch (err) {
      setError('इतिवृत्त फाईल अपलोड अयशस्वी: ' + (err.response?.data?.error || err.message));
    } finally {
      setUploadingMom(false);
      e.target.value = '';
    }
  };

  const handleSendMom = async () => {
    if (!window.confirm('या ग्रामसभेचे इतिवृत्त (MoM) आणि मंजूर झालेले सर्व ठराव WhatsApp द्वारे नागरिकांना पाठवायचे आहेत का?')) return;
    setSendingMom(true);
    setError('');
    setSuccess('');
    try {
      await gramsabhaAPI.sendMom(slug, selected.id, {
        minutes: momSummary,
        mom_attachment_url: momAttachmentUrl,
      });
      setSuccess('सभेचे इतिवृत्त (MoM) WhatsApp वर यशस्वीरित्या पाठवले!');
      const updatedRes = await gramsabhaAPI.getById(slug, selected.id);
      setSelected(updatedRes.data.meeting);
      loadData();
    } catch (err) {
      setError(extractError(err));
    } finally {
      setSendingMom(false);
    }
  };

  const [search, setSearch] = useState('');
  const [typeFilter, setTypeFilter] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [sort, setSort] = useState({ key: 'meeting_date', direction: 'desc' });
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);

  const filteredMeetings = useMemo(() => meetings.filter(m =>
    (!search || m.title.toLowerCase().includes(search.toLowerCase()) || m.venue.toLowerCase().includes(search.toLowerCase())) &&
    (!typeFilter || m.meeting_type === typeFilter) &&
    (!statusFilter || m.status === statusFilter)
  ).sort((a,b) => {
    const left = String(a[sort.key] || '').toLowerCase();
    const right = String(b[sort.key] || '').toLowerCase();
    return (left > right ? 1 : left < right ? -1 : 0) * (sort.direction === 'asc' ? 1 : -1);
  }), [meetings, search, typeFilter, statusFilter, sort]);

  const totalPages = Math.max(1, Math.ceil(filteredMeetings.length / pageSize));
  const visibleMeetings = filteredMeetings.slice((page - 1) * pageSize, page * pageSize);
  useEffect(() => { if (page > totalPages) setPage(totalPages); }, [page, totalPages]);

  const changeSort = key => {
    setSort(current => ({ key, direction: current.key === key && current.direction === 'asc' ? 'desc' : 'asc' }));
    setPage(1);
  };

  const SortHeader = ({ column, children }) => (
    <button className={`gramsabha-sort-header ${sort.key === column ? 'active' : ''}`} onClick={() => changeSort(column)}>
      {children}
      <span>{sort.key === column ? (sort.direction === 'asc' ? '↑' : '↓') : '↕'}</span>
    </button>
  );

  const exportCsv = () => {
    const cell = value => `"${String(value ?? '').replace(/"/g, '""')}"`;
    const rows = [
      ['Title', 'Type', 'Date', 'Time', 'Venue', 'Status', 'MoM Sent', 'Attachment'],
      ...filteredMeetings.map(item => [
        item.title,
        item.meeting_type,
        item.meeting_date,
        item.meeting_time,
        item.venue,
        item.status,
        item.mom_sent ? 'Yes' : 'No',
        item.attachment_url || '',
      ])
    ];
    const link = document.createElement('a');
    link.href = URL.createObjectURL(new Blob(['\ufeff', rows.map(row => row.map(cell).join(',')).join('\n')], { type: 'text/csv;charset=utf-8' }));
    link.download = `gramsabha-${new Date().toISOString().slice(0, 10)}.csv`;
    link.click();
    URL.revokeObjectURL(link.href);
  };

  const formatMeetingDateTime = (meeting) => {
    const date = meeting.meeting_date ? new Date(`${meeting.meeting_date}T00:00:00`).toLocaleDateString('mr-IN') : '-';
    const time = meeting.meeting_time ? String(meeting.meeting_time).slice(0, 5) : '';
    return time ? `${date} ${time}` : date;
  };
  const agendaText = (agenda) => Array.isArray(agenda) ? agenda.join(', ') : agenda;

  return (
    <div>
      <div className="admin-card-header">
        <div>
          <h2>🏛️ ग्रामसभा व्यवस्थापन</h2>
          <small style={{ color: '#64748b' }}>ग्रामसभा नियोजन, सूचना प्रसारण, ठराव व इतिवृत्त (MoM) व्यवस्थापन</small>
        </div>
        {canWriteThis && (
          <button className="btn btn-primary" onClick={showForm ? closeForm : openCreate}>
            {showForm ? 'रद्द करा' : '+ सभा नियोजित करा'}
          </button>
        )}
      </div>

      {success && <div className="alert alert-success">{success}</div>}
      {error && <div className="alert alert-error">{error}</div>}

      {showForm && (
        <div className="admin-card">
          <h3 style={{ marginTop: 0, marginBottom: 15 }}>नवीन ग्रामसभा नियोजन</h3>
          <form onSubmit={handleSubmit}>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 15 }}>
              <div className="form-group">
                <label>शीर्षक *</label>
                <input
                  value={formData.title}
                  placeholder="उदा. स्वातंत्र्य दिन विशेष ग्रामसभा"
                  onChange={e => setField('title', e.target.value)}
                  required
                  className={formErrors.title ? 'invalid' : ''}
                />
                {formErrors.title && <small className="admin-field-error">{formErrors.title}</small>}
              </div>

              <div className="form-group">
                <label>प्रकार</label>
                <select value={formData.meeting_type} onChange={e => setField('meeting_type', e.target.value)}>
                  <option value="regular">नियमित</option>
                  <option value="special">विशेष</option>
                  <option value="emergency">आपत्कालीन</option>
                </select>
              </div>

              <div className="form-group">
                <label>स्थिती (Status)</label>
                <select value={formData.status} onChange={e => setField('status', e.target.value)}>
                  <option value="scheduled">नियोजित (Scheduled)</option>
                  <option value="in_progress">प्रगतीपथावर (In Progress)</option>
                  <option value="completed">संपन्न (Completed)</option>
                  <option value="cancelled">रद्द (Cancelled)</option>
                </select>
              </div>

              <div className="form-group">
                <label>तारीख आणि वेळ *</label>
                <input
                  type="datetime-local"
                  value={formData.meeting_date}
                  onChange={e => setField('meeting_date', e.target.value)}
                  required
                  className={formErrors.meeting_date ? 'invalid' : ''}
                />
                {formErrors.meeting_date && <small className="admin-field-error">{formErrors.meeting_date}</small>}
              </div>

              <div className="form-group">
                <label>स्थळ *</label>
                <input
                  value={formData.venue}
                  placeholder="उदा. ग्रामपंचायत कार्यालय / समाज मंदिर"
                  onChange={e => setField('venue', e.target.value)}
                  required
                  className={formErrors.venue ? 'invalid' : ''}
                />
                {formErrors.venue && <small className="admin-field-error">{formErrors.venue}</small>}
              </div>

              {/* Notice / Agenda attachment */}
              <div className="form-group" style={{ gridColumn: 'span 2' }}>
                <label>📎 सभेची सूचना / अजेंडा फाईल (PDF किंवा फोटो - ऐच्छिक)</label>
                <div className="gramsabha-attach-box">
                  <div style={{ display: 'flex', alignItems: 'center', gap: 12, flexWrap: 'wrap' }}>
                    <input
                      type="file"
                      accept=".pdf,.png,.jpg,.jpeg,.webp"
                      onChange={handleNoticeUpload}
                      disabled={uploadingNotice}
                    />
                    {uploadingNotice && <span style={{ fontSize: 12, color: '#0284c7' }}>⏳ फाईल अपलोड होत आहे...</span>}
                    {formData.attachment_url && (
                      <div className="gramsabha-attach-preview">
                        <span>✓ फाईल जोडली:</span>
                        <a href={formData.attachment_url} target="_blank" rel="noreferrer" style={{ fontWeight: 600, color: '#0369a1' }}>
                          पहा / डाऊनलोड
                        </a>
                        <button
                          type="button"
                          onClick={() => setFormData(prev => ({ ...prev, attachment_url: '' }))}
                          style={{ border: 'none', background: 'none', cursor: 'pointer', color: '#dc2626', fontWeight: 'bold' }}
                          title="काढून टाका"
                        >
                          ✕
                        </button>
                      </div>
                    )}
                  </div>
                  <div style={{ marginTop: 6, fontSize: 11, color: '#64748b' }}>
                    * सभेची अधिकृत नोटीस किंवा अजेंडा PDF/फोटो जोडू शकता. WhatsApp द्वारे नागरिकांना पाठवताना ही फाईल संलग्न केली जाईल.
                  </div>
                </div>
              </div>

              <div className="form-group" style={{ gridColumn: 'span 2' }}>
                <label>कार्यसूची (Agenda) - प्रति ओळ एक विषय</label>
                <textarea
                  rows="3"
                  placeholder="१. मागील सभेचे इतिवृत्त वाचून मंजुरी देणे&#10;२. चालू आर्थिक वर्षातील विकास कामांवर चर्चा&#10;३. नवीन पाणीपुरवठा योजनेस मान्यता देणे"
                  value={formData.agenda}
                  onChange={e => setField('agenda', e.target.value)}
                />
              </div>

              <div className="form-group" style={{ gridColumn: 'span 2', background: '#f0fdf4', padding: '10px 14px', borderRadius: 8, border: '1px solid #bbf7d0' }}>
                <span style={{ fontSize: 12, color: '#166534', fontWeight: 600 }}>
                  📱 ग्रामसभा नियोजित केल्यानंतर आपोआप सर्व नागरिकांना WhatsApp वर सभेची नोटीस व तारीख/वेळ पाठवली जाईल.
                </span>
              </div>
            </div>

            <div className="admin-form-actions" style={{ marginTop: 15 }}>
              <button type="submit" className="btn btn-primary" disabled={uploadingNotice}>नियोजित करा</button>
              <button type="button" className="btn btn-outline" onClick={closeForm}>रद्द करा</button>
            </div>
          </form>
        </div>
      )}

      {!showForm && selected && (
        <div className="admin-card" style={{ border: '2px solid #2563eb' }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', flexWrap: 'wrap', gap: 10 }}>
            <div>
              <h2 style={{ margin: 0, color: '#1e3a8a' }}>{selected.title}</h2>
              <p style={{ margin: '6px 0 0', color: '#475569' }}>
                📅 {formatMeetingDateTime(selected)} | 📍 {selected.venue} | <span className={`badge badge-${STATUS_COLORS[selected.status] || 'info'}`}>{STATUS_LABELS[selected.status] || selected.status}</span>
              </p>
              <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginTop: 8 }}>
                <label style={{ fontSize: 12, fontWeight: 700, color: '#1e3a8a' }}>स्थिती बदला:</label>
                <select
                  value={selected.status}
                  onChange={async (e) => {
                    const newStatus = e.target.value;
                    try {
                      await gramsabhaAPI.update(slug, selected.id, { status: newStatus });
                      setSelected(prev => ({ ...prev, status: newStatus }));
                      setMeetings(prev => prev.map(m => m.id === selected.id ? { ...m, status: newStatus } : m));
                      setSuccess(`ग्रामसभा स्थिती '${STATUS_LABELS[newStatus] || newStatus}' मध्ये अद्ययावत केली.`);
                    } catch (err) {
                      setError('स्थिती अद्ययावत करताना त्रुटी आली.');
                    }
                  }}
                  style={{ padding: '4px 10px', borderRadius: 6, border: '1px solid #94a3b8', fontSize: 12, fontWeight: 600, background: '#f8fafc' }}
                >
                  <option value="scheduled">नियोजित (Scheduled)</option>
                  <option value="in_progress">प्रगतीपथावर (In Progress)</option>
                  <option value="completed">संपन्न (Completed)</option>
                  <option value="cancelled">रद्द (Cancelled)</option>
                </select>
              </div>
            </div>
            <button className="btn btn-outline btn-sm" onClick={() => setSelected(null)}>✕ बंद करा</button>
          </div>

          {selected.attachment_url && (
            <div style={{ marginTop: 12, padding: '8px 12px', background: '#eff6ff', borderRadius: 6, display: 'inline-flex', alignItems: 'center', gap: 8, fontSize: 12 }}>
              <span>📎 <strong>सभेची सूचना फाईल:</strong></span>
              <a href={selected.attachment_url} target="_blank" rel="noreferrer" style={{ color: '#2563eb', fontWeight: 600 }}>
                पहा / डाऊनलोड करा ↗
              </a>
            </div>
          )}

          {selected.agenda && (
            <div style={{ marginTop: 14 }}>
              <strong>कार्यसूची:</strong>
              <div style={{ marginTop: 4, padding: '8px 12px', background: '#f8fafc', borderRadius: 6, fontSize: 12 }}>
                {agendaText(selected.agenda)}
              </div>
            </div>
          )}

          {/* Resolutions */}
          <h3 style={{ marginTop: 20 }}>ठराव ({selected.GramsabhaResolutions?.length || 0})</h3>
          {selected.GramsabhaResolutions?.length === 0 && (
            <p style={{ color: '#94a3b8', fontSize: 12 }}>अद्याप कोणताही ठराव नोंदवलेला नाही.</p>
          )}
          {selected.GramsabhaResolutions?.map((r) => (
            <div key={r.id} style={{ padding: '12px 14px', borderBottom: '1px solid #e2e8f0', marginBottom: 8, background: '#fff', borderRadius: 6, border: '1px solid #edf2f7' }}>
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 8 }}>
                <strong style={{ color: '#1e40af', fontSize: 13 }}>{r.resolution_uid}: {r.title}</strong>
                <span style={{
                  display: 'inline-block',
                  padding: '2px 8px',
                  borderRadius: 4,
                  fontSize: 11,
                  fontWeight: 700,
                  background: r.result === 'rejected' ? '#fee2e2' : r.result === 'deferred' ? '#fef3c7' : '#dcfce7',
                  color: r.result === 'rejected' ? '#b91c1c' : r.result === 'deferred' ? '#b45309' : '#15803d',
                }}>
                  {r.result === 'rejected' ? 'नामंजूर (Rejected)' : r.result === 'deferred' ? 'स्थगित (Deferred)' : 'मंजूर (Passed)'}
                </span>
              </div>
              {r.description && <p style={{ margin: '6px 0', fontSize: 12, color: '#334155' }}>{r.description}</p>}
              <div style={{ color: '#64748b', fontSize: 11, display: 'flex', flexWrap: 'wrap', gap: '6px 14px', marginTop: 4 }}>
                <span><strong>प्रस्तावक:</strong> {r.proposed_by || '-'}</span>
                {r.seconded_by && <span><strong>अनुमोदक:</strong> {r.seconded_by}</span>}
                <span>
                  <strong>मतदान:</strong> समर्थन: <strong style={{ color: '#15803d' }}>{r.votes_for || 0}</strong>, विरोध: <strong style={{ color: '#b91c1c' }}>{r.votes_against || 0}</strong>
                  {r.votes_abstain > 0 && <>, तटस्थ: <strong>{r.votes_abstain}</strong></>}
                </span>
              </div>
            </div>
          ))}

          {canWriteThis && (
            <div style={{ marginTop: 16, padding: '14px', background: '#f8fafc', borderRadius: 8, border: '1px solid #e2e8f0' }}>
              <h4 style={{ margin: '0 0 10px', fontSize: 13, fontWeight: 700, color: '#0f172a' }}>+ नवीन ठराव जोडा</h4>
              <form onSubmit={handleResolution}>
                <div style={{ display: 'grid', gridTemplateColumns: '2fr 1fr', gap: 10 }}>
                  <div className="form-group">
                    <label>ठरावाचे शीर्षक *</label>
                    <input
                      value={resolutionData.title}
                      placeholder="उदा. ग्राम स्वच्छता मोहिमेस मंजुरी"
                      onChange={e => setResolutionData({...resolutionData, title: e.target.value})}
                      required
                    />
                  </div>
                  <div className="form-group">
                    <label>निर्णय / स्थिती *</label>
                    <select
                      value={resolutionData.result}
                      onChange={e => setResolutionData({...resolutionData, result: e.target.value})}
                    >
                      <option value="passed">मंजूर (Passed)</option>
                      <option value="rejected">नामंजूर (Rejected)</option>
                      <option value="deferred">स्थगित (Deferred)</option>
                    </select>
                  </div>
                </div>

                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10, marginTop: 6 }}>
                  <div className="form-group">
                    <label>प्रस्तावक</label>
                    <input
                      value={resolutionData.proposed_by}
                      placeholder="उदा. सरपंच / उपसरपंच / नागरिक"
                      onChange={e => setResolutionData({...resolutionData, proposed_by: e.target.value})}
                    />
                  </div>
                  <div className="form-group">
                    <label>अनुमोदक</label>
                    <input
                      value={resolutionData.seconded_by}
                      placeholder="उदा. उपसरपंच / सदस्य नाव"
                      onChange={e => setResolutionData({...resolutionData, seconded_by: e.target.value})}
                    />
                  </div>
                </div>

                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 10, marginTop: 6 }}>
                  <div className="form-group">
                    <label>मतदान - समर्थन (In Favor)</label>
                    <input
                      type="number"
                      min="0"
                      value={resolutionData.votes_for}
                      placeholder="उदा. ४५"
                      onChange={e => setResolutionData({...resolutionData, votes_for: e.target.value})}
                    />
                  </div>
                  <div className="form-group">
                    <label>मतदान - विरोध (Against)</label>
                    <input
                      type="number"
                      min="0"
                      value={resolutionData.votes_against}
                      placeholder="उदा. २"
                      onChange={e => setResolutionData({...resolutionData, votes_against: e.target.value})}
                    />
                  </div>
                  <div className="form-group">
                    <label>तटस्थ / गैरहजर (Abstain)</label>
                    <input
                      type="number"
                      min="0"
                      value={resolutionData.votes_abstain}
                      placeholder="उदा. ०"
                      onChange={e => setResolutionData({...resolutionData, votes_abstain: e.target.value})}
                    />
                  </div>
                </div>

                <div className="form-group" style={{ marginTop: 6 }}>
                  <label>वर्णन</label>
                  <textarea
                    rows="2"
                    placeholder="ठरावाचा सविस्तर तपशील..."
                    value={resolutionData.description}
                    onChange={e => setResolutionData({...resolutionData, description: e.target.value})}
                  />
                </div>
                <button type="submit" className="btn btn-primary btn-sm" style={{ marginTop: 8 }}>ठराव जोडा</button>
              </form>
            </div>
          )}

          {/* MoM (Minutes of Meeting / इतिवृत्त) Section */}
          <div className="mom-section-card">
            <div className="mom-header">
              <div>
                <h3 style={{ margin: 0, display: 'inline-flex', alignItems: 'center', gap: 8 }}>
                  <span>📝 सभेचे इतिवृत्त (Minutes of Meeting - MoM)</span>
                </h3>
              </div>
              <div>
                {selected.mom_sent ? (
                  <span className="mom-badge sent">
                    ✓ WhatsApp वर पाठवले ({selected.mom_sent_at ? formatMarathiDate(selected.mom_sent_at) : 'होय'})
                  </span>
                ) : (
                  <span className="mom-badge pending">⏳ इतिवृत्त WhatsApp वर पाठवणे बाकी आहे</span>
                )}
              </div>
            </div>

            <div style={{ display: 'grid', gridTemplateColumns: '1fr', gap: 12 }}>
              <div className="form-group">
                <label>इतिवृत्त सारांश (MoM Summary) *</label>
                <textarea
                  rows="4"
                  placeholder="सभेत झालेल्या मुख्य निर्णयांचा व चर्चांचा सारांश येथे लिहा..."
                  value={momSummary}
                  onChange={e => setMomSummary(e.target.value)}
                  disabled={!canWriteThis}
                />
              </div>

              <div className="form-group">
                <label>📎 इतिवृत्त अधिकृत प्रत (PDF किंवा फोटो - ऐच्छिक)</label>
                <div className="gramsabha-attach-box">
                  <div style={{ display: 'flex', alignItems: 'center', gap: 12, flexWrap: 'wrap' }}>
                    {canWriteThis && (
                      <input
                        type="file"
                        accept=".pdf,.png,.jpg,.jpeg,.webp"
                        onChange={handleMomUpload}
                        disabled={uploadingMom}
                      />
                    )}
                    {uploadingMom && <span style={{ fontSize: 12, color: '#0284c7' }}>⏳ फाईल अपलोड होत आहे...</span>}
                    {momAttachmentUrl && (
                      <div className="gramsabha-attach-preview">
                        <span>✓ इतिवृत्त फाईल:</span>
                        <a href={momAttachmentUrl} target="_blank" rel="noreferrer" style={{ fontWeight: 600, color: '#0369a1' }}>
                          पहा / डाऊनलोड
                        </a>
                        {canWriteThis && (
                          <button
                            type="button"
                            onClick={() => setMomAttachmentUrl('')}
                            style={{ border: 'none', background: 'none', cursor: 'pointer', color: '#dc2626', fontWeight: 'bold' }}
                            title="काढून टाका"
                          >
                            ✕
                          </button>
                        )}
                      </div>
                    )}
                  </div>
                  <div style={{ marginTop: 4, fontSize: 11, color: '#64748b' }}>
                    * स्वाक्षरी केलेले इतिवृत्त रजिस्टर किंवा PDF कागदपत्र येथे जोडू शकता. WhatsApp वर नागरिकांना हा कागदपत्र थेट पाठवला जाईल.
                  </div>
                </div>
              </div>

              {canWriteThis && (
                <div style={{ marginTop: 6, display: 'flex', alignItems: 'center', gap: 12, flexWrap: 'wrap' }}>
                  <button
                    type="button"
                    className="btn btn-primary"
                    disabled={sendingMom || uploadingMom}
                    onClick={handleSendMom}
                    style={{ background: '#15803d', borderColor: '#15803d' }}
                  >
                    {sendingMom ? '⏳ WhatsApp वर पाठवत आहे...' : '📢 इतिवृत्त (MoM) WhatsApp वर पाठवा'}
                  </button>
                  <span style={{ fontSize: 11, color: '#475569' }}>
                    (सभेचे इतिवृत्त सारांश व मंजूर झालेले सर्व {selected.GramsabhaResolutions?.length || 0} ठराव नागरिकांना पाठवले जातील)
                  </span>
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {!showForm && (
        <div className="admin-card gramsabha-table-card">
          <div className="gramsabha-table-toolbar">
            <label className="gramsabha-search">
              <span>⌕</span>
              <input
                placeholder="शीर्षक किंवा स्थळाने शोधा..."
                value={search}
                onChange={e => { setSearch(e.target.value); setPage(1); }}
              />
            </label>
            <select value={typeFilter} onChange={e => { setTypeFilter(e.target.value); setPage(1); }}>
              <option value="">सर्व प्रकार</option>
              <option value="regular">नियमित</option>
              <option value="special">विशेष</option>
              <option value="emergency">आपत्कालीन</option>
            </select>
            <select value={statusFilter} onChange={e => { setStatusFilter(e.target.value); setPage(1); }}>
              <option value="">सर्व स्थिती</option>
              <option value="scheduled">नियोजित</option>
              <option value="in_progress">प्रगतीपथावर</option>
              <option value="completed">पूर्ण</option>
              <option value="cancelled">रद्द</option>
            </select>
            <button className="gramsabha-export-btn" onClick={exportCsv}>⇩ CSV निर्यात</button>
          </div>

          <div className="gramsabha-table-meta">
            <span><strong>{filteredMeetings.length}</strong> ग्रामसभा नोंदी</span>
            <span className={`gramsabha-load-status ${loading ? 'visible' : ''}`}><i /> माहिती अद्ययावत होत आहे</span>
          </div>

          <div className={`table-scroll gramsabha-table-scroll ${loading ? 'is-refreshing' : ''}`}>
            <table className="data-table gramsabha-data-table">
              <thead>
                <tr>
                  <th><SortHeader column="title">शीर्षक</SortHeader></th>
                  <th><SortHeader column="meeting_type">प्रकार</SortHeader></th>
                  <th><SortHeader column="meeting_date">तारीख / वेळ</SortHeader></th>
                  <th><SortHeader column="venue">स्थळ</SortHeader></th>
                  <th>संलग्नीत फाईल</th>
                  <th>इतिवृत्त (MoM)</th>
                  <th><SortHeader column="status">स्थिती</SortHeader></th>
                  <th>कृती</th>
                </tr>
              </thead>
              <tbody>
                {loading ? (
                  <LoadingSpinner.TableRow colSpan={8} message="ग्रामसभा माहिती लोड होत आहे..." />
                ) : visibleMeetings.length === 0 ? (
                  <tr><td colSpan="8" className="gramsabha-empty">ग्रामसभा नोंदी सापडल्या नाहीत.</td></tr>
                ) : (
                  visibleMeetings.map((m) => (
                  <tr key={m.id}>
                    <td>
                      <div className="gramsabha-title-cell">
                        <strong>{m.title}</strong>
                        <small>{agendaText(m.agenda)?.slice(0, 70) || 'कार्यसूची उपलब्ध नाही'}</small>
                      </div>
                    </td>
                    <td><span className="gramsabha-type">{m.meeting_type}</span></td>
                    <td className="gramsabha-date">{formatMeetingDateTime(m)}</td>
                    <td>{m.venue}</td>
                    <td>
                      {m.attachment_url ? (
                        <a
                          href={m.attachment_url}
                          target="_blank"
                          rel="noreferrer"
                          style={{ fontSize: 11, color: '#0284c7', textDecoration: 'none', display: 'inline-flex', alignItems: 'center', gap: 4 }}
                        >
                          📎 <span>पहा</span>
                        </a>
                      ) : (
                        <span style={{ color: '#94a3b8', fontSize: 11 }}>-</span>
                      )}
                    </td>
                    <td>
                      {m.mom_sent ? (
                        <span style={{ fontSize: 11, color: '#15803d', fontWeight: 600 }}>✓ पाठवले</span>
                      ) : (
                        <span style={{ fontSize: 11, color: '#94a3b8' }}>बाकी</span>
                      )}
                    </td>
                    <td><span className={`gramsabha-status status-${m.status}`}>{STATUS_LABELS[m.status] || m.status?.replace(/_/g, ' ')}</span></td>
                    <td><button className="gramsabha-view-btn" onClick={() => viewDetails(m.id)}>तपशील →</button></td>
                  </tr>
                ))
              )}
              </tbody>
            </table>
          </div>

          <div className="table-pagination gramsabha-pagination">
            <label>
              प्रति पृष्ठ{' '}
              <select value={pageSize} onChange={e => { setPageSize(Number(e.target.value)); setPage(1); }}>
                <option>10</option>
                <option>20</option>
                <option>50</option>
              </select>
            </label>
            <button className="btn btn-outline btn-sm" disabled={page === 1} onClick={() => setPage(value => Math.max(1, value - 1))}>
              ← मागील
            </button>
            <span>पृष्ठ <strong>{page}</strong> / {totalPages}</span>
            <button className="btn btn-outline btn-sm" disabled={page >= totalPages} onClick={() => setPage(value => Math.min(totalPages, value + 1))}>
              पुढील →
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
